import UploadFileIcon from "@mui/icons-material/UploadFile";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  Typography,
  type SxProps,
  type Theme,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useDropzone } from "react-dropzone";
import {
  DocumentationAdminTopicsDocument,
  DocumentationItemStatus,
  useDocumentationAdminCreateItemMutation,
  useDocumentationAdminItemQuery,
  useDocumentationAdminUpdateItemMutation,
} from "../../../generated/graphql";
import { throwOnGraphqlErrors } from "../../api-keys-admin/throw-on-graphql-errors";
import { readDocumentationFileMetadata } from "../documentation-file-metadata";
import DocumentationPreview from "./documentation-preview";

/** Matches the backend's `DOCUMENTATION_HTML_MAX_LENGTH`. */
const MAX_HTML_LENGTH = 2_000_000;
const SLUG_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
const ACCEPTED_FILES = { "text/html": [".html", ".htm"] };

const styles = {
  layout: {
    display: "grid",
    gridTemplateColumns: { xs: "1fr", md: "320px 1fr" },
    gap: 3,
    pt: 1,
  } satisfies SxProps<Theme>,
  dropzone: {
    border: 2,
    borderStyle: "dashed",
    borderColor: "divider",
    borderRadius: 1,
    p: 2,
    textAlign: "center",
    cursor: "pointer",
  } satisfies SxProps<Theme>,
  dropzoneActive: {
    border: 2,
    borderStyle: "dashed",
    borderColor: "primary.main",
    borderRadius: 1,
    p: 2,
    textAlign: "center",
    cursor: "pointer",
    bgcolor: "action.hover",
  } satisfies SxProps<Theme>,
  emptyPreview: {
    height: "70vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    border: 1,
    borderColor: "divider",
    borderRadius: 1,
  } satisfies SxProps<Theme>,
};

type TopicOption = { readonly uuid: string; readonly title: string };

type ItemDialogProps = {
  readonly open: boolean;
  /** The item being edited, or `null` to add one to `defaultTopicUuid`. */
  readonly itemUuid: string | null;
  readonly defaultTopicUuid: string;
  readonly topics: readonly TopicOption[];
  readonly onClose: () => void;
};

type UploadedFile = { readonly name: string; readonly html: string };

const ItemDialog = ({
  open,
  itemUuid,
  defaultTopicUuid,
  topics,
  onClose,
}: ItemDialogProps) => {
  const isCreate = itemUuid === null;
  const [topicUuid, setTopicUuid] = useState(defaultTopicUuid);
  const [title, setTitle] = useState("");
  const [slug, setSlug] = useState("");
  const [code, setCode] = useState("");
  const [upload, setUpload] = useState<UploadedFile | null>(null);
  const [error, setError] = useState<string | null>(null);

  const { data: itemData, loading: itemLoading } =
    useDocumentationAdminItemQuery({
      variables: { uuid: itemUuid ?? "" },
      skip: !open || isCreate,
      fetchPolicy: "network-only",
    });
  const existing = itemData?.documentationAdminItem;

  useEffect(() => {
    if (!open) {
      return;
    }
    setUpload(null);
    setError(null);
    setTopicUuid(existing?.topicUuid ?? defaultTopicUuid);
    setTitle(existing?.title ?? "");
    setSlug(existing?.slug ?? "");
    setCode(existing?.code ?? "");
  }, [open, existing, defaultTopicUuid]);

  const refetchQueries = [DocumentationAdminTopicsDocument];
  const [createItem, { loading: creating }] =
    useDocumentationAdminCreateItemMutation({ refetchQueries });
  const [updateItem, { loading: updating }] =
    useDocumentationAdminUpdateItemMutation({ refetchQueries });

  const onDrop = async (files: File[]) => {
    const [file] = files;
    if (file === undefined) {
      return;
    }
    const html = await file.text();
    if (html.length > MAX_HTML_LENGTH) {
      setError(
        `${file.name} is ${Math.round(html.length / 1000)} KB; the limit is ${MAX_HTML_LENGTH / 1_000_000} MB.`,
      );
      return;
    }
    setError(null);
    setUpload({ name: file.name, html });
    if (isCreate) {
      const metadata = readDocumentationFileMetadata(file.name, html);
      setTitle(metadata.title);
      setSlug(metadata.slug);
      setCode(metadata.code ?? "");
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: (files) => {
      void onDrop(files);
    },
    accept: ACCEPTED_FILES,
    maxFiles: 1,
  });

  const previewHtml = upload?.html ?? existing?.html ?? null;
  const slugIsValid = SLUG_PATTERN.test(slug);
  const canSave =
    title.trim() !== "" &&
    slugIsValid &&
    (isCreate ? upload !== null : existing !== undefined) &&
    !creating &&
    !updating;

  const handleSave = async () => {
    setError(null);
    const trimmedCode = code.trim();
    try {
      if (isCreate) {
        if (upload === null) {
          return;
        }
        const result = await createItem({
          variables: {
            input: {
              topicUuid,
              title: title.trim(),
              slug,
              code: trimmedCode === "" ? null : trimmedCode,
              html: upload.html,
            },
          },
        });
        throwOnGraphqlErrors(result);
      } else {
        const result = await updateItem({
          variables: {
            input: {
              uuid: itemUuid,
              topicUuid,
              title: title.trim(),
              slug,
              code: trimmedCode === "" ? null : trimmedCode,
              html: upload?.html,
            },
          },
        });
        throwOnGraphqlErrors(result);
      }
      onClose();
    } catch (saveError) {
      setError(
        saveError instanceof Error ? saveError.message : String(saveError),
      );
    }
  };

  return (
    <Dialog fullWidth maxWidth="xl" open={open} onClose={onClose}>
      <DialogTitle>{isCreate ? "New document" : "Edit document"}</DialogTitle>
      <DialogContent>
        {itemLoading ? (
          <CircularProgress />
        ) : (
          <Box sx={styles.layout}>
            <Stack spacing={2}>
              {error !== null && <Alert severity="error">{error}</Alert>}
              <Box
                {...getRootProps()}
                sx={isDragActive ? styles.dropzoneActive : styles.dropzone}
              >
                <input {...getInputProps()} />
                <UploadFileIcon color="action" />
                <Typography variant="body2">
                  {upload?.name ??
                    (isCreate
                      ? "Drop the .html file here, or click to choose it"
                      : "Drop a new .html file to replace the document")}
                </Typography>
              </Box>
              <TextField
                select
                label="Topic"
                value={topicUuid}
                onChange={(event) => {
                  setTopicUuid(event.target.value);
                }}
              >
                {topics.map((topic) => (
                  <MenuItem key={topic.uuid} value={topic.uuid}>
                    {topic.title}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                required
                label="Title"
                value={title}
                inputProps={{ maxLength: 200 }}
                onChange={(event) => {
                  setTitle(event.target.value);
                }}
              />
              <TextField
                label="Code"
                placeholder="ADM-CFG-5"
                value={code}
                inputProps={{ maxLength: 32 }}
                onChange={(event) => {
                  setCode(event.target.value);
                }}
              />
              <TextField
                required
                label="Slug"
                value={slug}
                error={slug !== "" && !slugIsValid}
                helperText="The file name without .html. Other documents link to this one through it."
                inputProps={{ maxLength: 120 }}
                onChange={(event) => {
                  setSlug(event.target.value);
                }}
              />
              {existing?.status === DocumentationItemStatus.Published ? (
                <Alert severity="warning">
                  This document is published. Saving changes what customers see
                  straight away.
                </Alert>
              ) : (
                !isCreate && (
                  <Typography variant="caption" color="text.secondary">
                    Publish it from the document list once it is ready.
                  </Typography>
                )
              )}
            </Stack>
            {previewHtml === null ? (
              <Box sx={styles.emptyPreview}>
                <Typography color="text.secondary">
                  The preview appears once a file is chosen.
                </Typography>
              </Box>
            ) : (
              <DocumentationPreview html={previewHtml} />
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          disabled={!canSave}
          onClick={() => {
            void handleSave();
          }}
        >
          {isCreate ? "Save as draft" : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ItemDialog;
